/**
 * OwlbertChat — the primary conversational surface.
 *
 * Two modes via the `scope` prop:
 *   - "workspace" → /api/workspaces/:wsId/chat/*  (per-client Owlbert)
 *   - "operator"  → /api/operator/chat/*           (cross-client meta-Owlbert)
 *
 * Pure Owlbert face. Sub-agents invisible. Creative loading copy while he thinks.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { apiGet, apiPost, apiDelete } from "@/lib/api";
import { toast } from "sonner";
import {
  Send,
  Plus,
  Trash2,
  MessageCircle,
  Save,
  RefreshCw,
  ArrowRight,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  Copy,
  Check as CheckIcon,
  FileText,
} from "lucide-react";

import { OWLBERT_DARK } from "@/lib/owlbert";
const OWL_PORTRAIT = OWLBERT_DARK;

// Creative loading phrases — cycle while Owlbert thinks
const LOADING_PHRASES = [
  "owlbert is thinking…",
  "consulting the brain…",
  "checking what worked last tuesday…",
  "rifling through the voice archive…",
  "drafting in your tone…",
  "looking at the content board…",
  "running the numbers…",
  "pulling the strategy doc…",
  "sharpening the hook…",
  "checking for AI-tells…",
  "asking the librarian…",
  "scanning recent engagement…",
  "comparing to your last 30 days…",
  "weighing two angles…",
  "killing the leverage word…",
];

// Suggested starter prompts by scope
const STARTERS_WORKSPACE = [
  "what should i post this week?",
  "draft a contrarian post about [topic]",
  "what's working this month?",
  "who engaged recently?",
  "show me overdue posts",
  "what's on my content board this week?",
  "remember that the client hates the word 'leverage'",
];

const STARTERS_OPERATOR = [
  "which clients need my attention today?",
  "what's the status of leansuite?",
  "summarize my whole book of business",
  "where am i overcapacity?",
];

function useApiBase(scope, workspaceId) {
  return useMemo(
    () =>
      scope === "operator"
        ? "/operator/chat"
        : `/workspaces/${workspaceId}/chat`,
    [scope, workspaceId],
  );
}

export default function OwlbertChat({ scope = "workspace" }) {
  const params = useParams();
  const navigate = useNavigate();
  const workspaceId = params.workspaceId || null;
  const api = useApiBase(scope, workspaceId);

  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [composing, setComposing] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingPhrase, setLoadingPhrase] = useState(LOADING_PHRASES[0]);
  const scrollRef = useRef(null);
  const composerRef = useRef(null);

  // Cycle loading phrases every 2.4s while sending
  useEffect(() => {
    if (!sending) return;
    const t = setInterval(() => {
      setLoadingPhrase((p) => {
        const others = LOADING_PHRASES.filter((x) => x !== p);
        return others[Math.floor(Math.random() * others.length)];
      });
    }, 2400);
    return () => clearInterval(t);
  }, [sending]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const loadThreads = async () => {
    try {
      const r = await apiGet(`${api}/threads`);
      setThreads(r || []);
      return r || [];
    } catch (e) {
      return [];
    }
  };

  const loadMessages = async (tid) => {
    try {
      const r = await apiGet(`${api}/threads/${tid}/messages`);
      setMessages(r || []);
    } catch (e) {
      toast.error("could not load conversation");
    }
  };

  const createThread = async () => {
    try {
      const t = await apiPost(`${api}/threads`, {});
      setThreads((prev) => [t, ...prev]);
      setActiveThread(t);
      setMessages([]);
      setTimeout(() => composerRef.current?.focus(), 100);
      return t;
    } catch (e) {
      toast.error("could not start conversation");
      return null;
    }
  };

  // Boot
  useEffect(() => {
    (async () => {
      const list = await loadThreads();
      if (list.length > 0) {
        setActiveThread(list[0]);
        loadMessages(list[0].thread_id);
      } else {
        const t = await createThread();
        if (t) setMessages([]);
      }
    })();
    // eslint-disable-next-line
  }, [scope, workspaceId]);

  const openThread = (t) => {
    setActiveThread(t);
    loadMessages(t.thread_id);
  };

  const deleteThread = async (tid) => {
    if (!window.confirm("delete this conversation?")) return;
    try {
      await apiDelete(`${api}/threads/${tid}`);
      const remaining = threads.filter((t) => t.thread_id !== tid);
      setThreads(remaining);
      if (activeThread?.thread_id === tid) {
        if (remaining[0]) {
          openThread(remaining[0]);
        } else {
          const t = await createThread();
          if (t) setMessages([]);
        }
      }
    } catch (e) {
      toast.error("delete failed");
    }
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? composing).trim();
    if (!text || sending) return;
    let thread = activeThread;
    if (!thread) {
      thread = await createThread();
      if (!thread) return;
    }
    const tempUser = {
      message_id: `tmp_${Date.now()}`,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, tempUser]);
    setComposing("");
    setSending(true);
    setLoadingPhrase(LOADING_PHRASES[Math.floor(Math.random() * LOADING_PHRASES.length)]);
    try {
      const r = await apiPost(`${api}/threads/${thread.thread_id}/messages`, {
        content: text,
      });
      // Backend returns immediately with a `pending` owlbert message.
      // Show it + poll its status until pending=False (or timeout).
      setMessages((m) => {
        const without = m.filter((x) => x.message_id !== tempUser.message_id);
        return [...without, r.user_message, r.owlbert_message];
      });
      loadThreads();

      const pendingId = r.pending_message_id || r.owlbert_message?.message_id;
      if (pendingId && r.owlbert_message?.pending) {
        await pollPendingMessage(thread.thread_id, pendingId);
      } else {
        setSending(false);
      }
    } catch (e) {
      setMessages((m) => m.filter((x) => x.message_id !== tempUser.message_id));
      toast.error(e?.response?.data?.detail || "owlbert didn't reply — try again");
      setSending(false);
    }
  };

  // Poll the pending owlbert message every 2s, up to 3 minutes. Cloudflare's
  // 30s cap doesn't apply here — each individual request is sub-second.
  const pollPendingMessage = async (threadId, messageId) => {
    const startedAt = Date.now();
    const maxMs = 3 * 60 * 1000;
    while (Date.now() - startedAt < maxMs) {
      await new Promise((res) => setTimeout(res, 2000));
      try {
        const m = await apiGet(`${api}/threads/${threadId}/messages/${messageId}/status`);
        if (m && !m.pending) {
          setMessages((prev) =>
            prev.map((x) => (x.message_id === messageId ? m : x))
          );
          setSending(false);
          return;
        }
      } catch (e) {
        // 404 / network blip — keep polling
      }
    }
    setSending(false);
    toast.error("owlbert is taking longer than usual. refresh in a moment.");
  };

  const onActionClick = async (action, payload) => {
    if (action === "save_to_pipeline") {
      if (!workspaceId) return;
      try {
        const r = await apiPost(`/workspaces/${workspaceId}/posts`, {
          title: (payload.topic || "owlbert draft").slice(0, 80),
          post_caption: payload.caption || "",
          status: "draft",
        });
        toast.success("saved to content", {
          action: {
            label: "open",
            onClick: () => navigate(`/workspace/${workspaceId}/posts`),
          },
        });
      } catch (e) {
        toast.error("save failed");
      }
    } else if (action === "regenerate") {
      send(`redraft that post — ${payload.topic}. fresh angle.`);
    } else if (action === "compose_idea") {
      const ideas = payload.ideas || [];
      const idx = payload.idea_index || 0;
      const idea = ideas[idx];
      if (idea) send(`draft a full post for this idea: ${idea.hook} — ${idea.angle}`);
    } else if (action === "open_path" && payload.path) {
      navigate(payload.path);
    }
  };

  const starters = scope === "operator" ? STARTERS_OPERATOR : STARTERS_WORKSPACE;

  return (
    <Layout
      fill
      title={scope === "operator" ? "AI Assistant — All Clients" : "AI Assistant"}
      subtitle={
        scope === "operator"
          ? "Ask questions across every client: performance, capacity, status."
          : "Ask for post drafts, strategy, or anything about this client."
      }
      actions={
        <button
          onClick={createThread}
          className="btn-ghost inline-flex items-center gap-1.5"
          data-testid="chat-new-thread"
        >
          <Plus className="w-3.5 h-3.5" /> New conversation
        </button>
      }
    >
      <div className="grid grid-cols-12 gap-0 hairline flex-1 min-h-0 overflow-hidden">
        {/* Thread sidebar */}
        <aside className="col-span-3 min-h-0 border-r border-hairline overflow-y-auto" data-testid="chat-threads">
          <div className="px-4 py-3 hairline-b">
            <div className="overline">Conversations ({threads.length})</div>
          </div>
          {threads.length === 0 ? (
            <div className="px-4 py-6 text-xs text-stone">No conversations yet</div>
          ) : (
            <ul className="divide-y divide-hairline">
              {threads.map((t) => (
                <li key={t.thread_id} className="group relative">
                  <button
                    onClick={() => openThread(t)}
                    data-testid={`chat-thread-${t.thread_id}`}
                    className={`w-full text-left px-4 py-3 hover:bg-graphite-2 transition-colors ${
                      activeThread?.thread_id === t.thread_id ? "bg-graphite-2 border-l-2 border-terracotta pl-[14px]" : ""
                    }`}
                  >
                    <div className="text-[13px] text-parchment truncate font-medium">
                      {t.title || "untitled"}
                    </div>
                    <div className="mono text-[10px] text-stone-dim mt-1">
                      {t.message_count || 0} msg · {fmtRel(t.last_message_at)}
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteThread(t.thread_id);
                    }}
                    className="absolute right-2 top-3 opacity-0 group-hover:opacity-100 p-1 text-stone-dim hover:text-terracotta transition-opacity"
                    data-testid={`chat-thread-delete-${t.thread_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Conversation surface — only this panel scrolls */}
        <section className="col-span-9 min-h-0 flex flex-col" data-testid="chat-conversation">
          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-8 py-8 space-y-8">
            {messages.length === 0 && !sending && (
              <EmptyConversation onPick={(s) => send(s)} starters={starters} />
            )}
            {messages.map((m) => (
              <Message
                key={m.message_id}
                msg={{ ...m, thread_id: activeThread?.thread_id }}
                onAction={onActionClick}
                workspaceId={workspaceId}
                scope={scope}
              />
            ))}
            {sending && <ThinkingBubble phrase={loadingPhrase} />}
          </div>

          {/* Composer */}
          <div className="hairline-t border-t border-hairline px-6 py-4 bg-graphite/40">
            <div className="relative">
              <textarea
                ref={composerRef}
                value={composing}
                onChange={(e) => setComposing(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !sending) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={
                  scope === "operator"
                    ? "Ask about any client… (Enter to send, Shift+Enter for a new line)"
                    : "Ask for a draft, strategy, or a memory… (Enter to send, Shift+Enter for a new line)"
                }
                rows={2}
                disabled={sending}
                data-testid="chat-composer"
                className="w-full input-block pr-14 resize-none"
              />
              <button
                onClick={() => send()}
                disabled={sending || !composing.trim()}
                data-testid="chat-send"
                className="absolute right-3 bottom-3 p-2 disabled:opacity-30 hover:text-terracotta transition-colors"
                style={{ color: composing.trim() ? "hsl(var(--accent))" : undefined }}
                title="send (⏎)"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
function EmptyConversation({ onPick, starters }) {
  return (
    <div className="max-w-2xl mx-auto text-center py-12 fade-up" data-testid="chat-empty">
      <img src={OWL_PORTRAIT} alt="Owlbert" className="owlbert-portrait w-20 h-20 object-cover mx-auto mb-6" />
      <h2 className="text-2xl font-extrabold text-parchment mb-3">What do you want to work on?</h2>
      <p className="text-sm text-stone mb-8 max-w-md mx-auto leading-relaxed">
        Ask for a post draft, a strategy review, or a client question. Answers use everything saved in this client's memory.
      </p>
      <div className="flex flex-wrap gap-2 justify-center max-w-xl mx-auto">
        {starters.map((s, i) => (
          <button
            key={i}
            onClick={() => onPick(s)}
            data-testid={`chat-starter-${i}`}
            className="hairline px-3 py-2 text-xs text-stone hover:text-parchment hover:border-hairline-hover transition-colors text-left"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Message({ msg, onAction, workspaceId, scope }) {
  const isUser = msg.role === "user";
  const [feedbackSent, setFeedbackSent] = useState(null); // "up" | "down" | null
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");

  const sendFeedback = async (rating, includeNote = false) => {
    try {
      await apiPost("/operator/feedback", {
        message_id: msg.message_id,
        thread_id: msg.thread_id || "",
        workspace_id: scope === "workspace" ? workspaceId : null,
        rating,
        note: includeNote ? note : "",
      });
      setFeedbackSent(rating);
      setNoteOpen(false);
      toast.success(rating === "up" ? "noted" : "noted — owlbert will learn");
    } catch (e) {
      toast.error("feedback failed");
    }
  };

  if (isUser) {
    return (
      <div className="flex justify-end fade-up" data-testid={`msg-${msg.message_id}`}>
        <div className="max-w-2xl">
          <div
            className="px-5 py-3 text-[15px] text-parchment leading-relaxed"
            style={{ background: "hsl(var(--accent) / 0.12)", border: "1px solid hsl(var(--accent) / 0.3)" }}
          >
            {msg.content}
          </div>
          <div className="mono text-[10px] text-stone-dim mt-1 text-right">{fmtRel(msg.created_at)}</div>
        </div>
      </div>
    );
  }
  // Owlbert message
  return (
    <div className="flex gap-4 fade-up" data-testid={`msg-${msg.message_id}`}>
      <img
        src={OWL_PORTRAIT}
        alt="Owlbert"
        className="owlbert-portrait w-10 h-10 object-cover shrink-0 mt-1"
      />
      <div className="flex-1 min-w-0 max-w-3xl">
        <div className="overline mb-2">owlbert</div>
        <div className="text-[15px] text-parchment leading-relaxed whitespace-pre-wrap break-words">
          {renderInline(msg.content || "")}
        </div>
        {msg.action_buttons && msg.action_buttons.length > 0 && msg.agent_used !== "compose" && (
          <div className="flex flex-wrap gap-2 mt-4" data-testid={`msg-actions-${msg.message_id}`}>
            {msg.action_buttons.map((b, i) => (
              <button
                key={i}
                onClick={() => onAction(b.action, b.payload || {})}
                data-testid={`msg-action-${msg.message_id}-${i}`}
                className="btn-ghost text-xs inline-flex items-center gap-1.5"
              >
                {iconForAction(b.action)}
                {b.label}
              </button>
            ))}
          </div>
        )}
        {/* Draft card — when sub-agent produced a post draft */}
        {msg.agent_used === "compose" && msg.structured?.caption && (
          <DraftCard
            caption={msg.structured.caption}
            hooks={msg.structured.hooks || []}
            topic={msg.structured.topic || ""}
            format={msg.structured.format || "text"}
            onSaveToContent={() =>
              onAction("save_to_pipeline", {
                caption: msg.structured.caption,
                topic: msg.structured.topic,
                format: msg.structured.format,
              })
            }
            onRegenerate={() =>
              onAction("regenerate", {
                topic: msg.structured.topic,
                format: msg.structured.format,
              })
            }
            messageId={msg.message_id}
          />
        )}
        {/* feedback chip */}
        <div className="mt-3 inline-flex items-center gap-1.5">
          <span className="mono text-[10px] text-stone-dim mr-1">{fmtRel(msg.created_at)}</span>
          <button
            onClick={() => sendFeedback("up")}
            disabled={feedbackSent !== null}
            data-testid={`msg-feedback-up-${msg.message_id}`}
            className="p-1 text-stone-dim hover:text-parchment disabled:opacity-100 transition-colors"
            style={feedbackSent === "up" ? { color: "hsl(var(--sage))" } : undefined}
            title="this landed"
          >
            <ThumbsUp className="w-3 h-3" />
          </button>
          <button
            onClick={() => setNoteOpen((v) => !v)}
            disabled={feedbackSent !== null}
            data-testid={`msg-feedback-down-${msg.message_id}`}
            className="p-1 text-stone-dim hover:text-terracotta disabled:opacity-100 transition-colors"
            style={feedbackSent === "down" ? { color: "hsl(var(--accent))" } : undefined}
            title="off — tell owlbert why"
          >
            <ThumbsDown className="w-3 h-3" />
          </button>
        </div>
        {noteOpen && feedbackSent === null && (
          <div className="mt-2 space-y-2 max-w-md" data-testid={`msg-feedback-form-${msg.message_id}`}>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="what was off? (logs as [feedback] memory)"
              rows={2}
              className="input-block resize-none text-xs"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => sendFeedback("down", true)}
                className="btn-primary text-xs"
                data-testid={`msg-feedback-submit-${msg.message_id}`}
              >
                send
              </button>
              <button onClick={() => setNoteOpen(false)} className="btn-text text-xs">
                cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function iconForAction(a) {
  if (a === "save_to_pipeline") return <Save className="w-3 h-3" />;
  if (a === "regenerate") return <RefreshCw className="w-3 h-3" />;
  if (a === "compose_idea") return <Sparkles className="w-3 h-3" />;
  if (a === "open_path") return <ArrowRight className="w-3 h-3" />;
  return null;
}

// minimal inline markdown — blockquote ">" lines + bold **x**
function renderInline(text) {
  const lines = text.split("\n");
  return lines.map((line, i) => {
    if (line.startsWith("> ")) {
      return (
        <div
          key={i}
          className="owlbert-voice my-2"
          style={{ fontSize: 16, marginLeft: 0 }}
        >
          {boldify(line.slice(2))}
        </div>
      );
    }
    return (
      <div key={i}>
        {boldify(line)}
        {i < lines.length - 1 && <br />}
      </div>
    );
  });
}

function boldify(text) {
  const parts = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<strong key={key++}>{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// ---------------------------------------------------------------------------
function ThinkingBubble({ phrase }) {
  return (
    <div className="flex gap-4 fade-up" data-testid="chat-thinking">
      <img
        src={OWL_PORTRAIT}
        alt="Owlbert"
        className="owlbert-portrait w-10 h-10 object-cover shrink-0 mt-1"
        style={{ animation: "pulse 1.8s ease-in-out infinite" }}
      />
      <div className="flex-1">
        <div className="overline mb-2">owlbert</div>
        <div className="inline-flex items-center gap-3">
          <div className="flex gap-1">
            <Dot delay={0} />
            <Dot delay={0.18} />
            <Dot delay={0.36} />
          </div>
          <span
            className="text-sm italic font-semibold"
            style={{ color: "hsl(var(--accent))" }}
            data-testid="chat-loading-phrase"
          >
            {phrase}
          </span>
        </div>
      </div>
    </div>
  );
}

function Dot({ delay }) {
  return (
    <span
      className="w-1.5 h-1.5 inline-block"
      style={{
        background: "hsl(var(--accent))",
        animation: `bouncedot 1s ease-in-out ${delay}s infinite`,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
function DraftCard({ caption, hooks, topic, format, onSaveToContent, onRegenerate, messageId }) {
  const [copied, setCopied] = useState(false);
  const [copiedHook, setCopiedHook] = useState(null);

  const copyText = async (text, which = "caption") => {
    try {
      await navigator.clipboard.writeText(text);
      if (which === "caption") {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } else {
        setCopiedHook(which);
        setTimeout(() => setCopiedHook(null), 1800);
      }
    } catch (e) {
      toast.error("clipboard blocked — select and copy manually");
    }
  };

  // Word + char count for the LinkedIn limit awareness (3000 chars)
  const charCount = caption.length;
  const wordCount = caption.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div
      className="hairline mt-5 fade-up"
      style={{ background: "hsl(var(--graphite-2))" }}
      data-testid={`draft-card-${messageId}`}
    >
      {/* header */}
      <div className="px-5 py-3 hairline-b flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-3.5 h-3.5 text-stone shrink-0" strokeWidth={1.5} />
          <span className="overline">draft</span>
          {format && format !== "text" && (
            <span className="tag-chip" data-tag="voice">
              {format}
            </span>
          )}
          {topic && (
            <span className="text-xs text-stone truncate hidden sm:inline">· {topic}</span>
          )}
        </div>
        <div className="mono text-[10px] text-stone-dim shrink-0">
          {wordCount}w · {charCount}/3000
        </div>
      </div>

      {/* body — clean reading surface */}
      <div
        className="px-7 py-6 text-[15px] leading-[1.65] text-parchment whitespace-pre-wrap break-words"
        data-testid={`draft-card-body-${messageId}`}
      >
        {caption}
      </div>

      {/* hooks (if any) */}
      {hooks && hooks.length > 0 && (
        <div className="hairline-t px-5 py-4 space-y-2">
          <div className="overline">alternative hooks</div>
          {hooks.slice(0, 3).map((h, i) => {
            // h may be a string OR { label, text }
            const text = typeof h === "string" ? h : h?.text || "";
            const label = typeof h === "object" ? h?.label : "";
            if (!text) return null;
            return (
              <div
                key={i}
                className="flex items-start gap-3 group"
                data-testid={`draft-hook-${messageId}-${i}`}
              >
                <span className="mono text-[10px] text-stone-dim mt-1 shrink-0">{i + 1}</span>
                <div className="flex-1 text-sm text-stone leading-relaxed">
                  {label && (
                    <span className="mono text-[9px] uppercase tracking-wider text-stone-dim mr-2">
                      {label}
                    </span>
                  )}
                  {text}
                </div>
                <button
                  onClick={() => copyText(text, `hook-${i}`)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-stone-dim hover:text-parchment transition-opacity"
                  data-testid={`draft-hook-copy-${messageId}-${i}`}
                  title="copy this hook"
                >
                  {copiedHook === `hook-${i}` ? (
                    <CheckIcon className="w-3 h-3" style={{ color: "hsl(var(--accent))" }} />
                  ) : (
                    <Copy className="w-3 h-3" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* actions */}
      <div className="hairline-t px-5 py-3 flex items-center gap-2 flex-wrap">
        <button
          onClick={() => copyText(caption, "caption")}
          className={`btn-primary text-xs inline-flex items-center gap-1.5 ${copied ? "opacity-70" : ""}`}
          data-testid={`draft-copy-${messageId}`}
        >
          {copied ? <CheckIcon className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copied" : "Copy post"}
        </button>
        <button
          onClick={onSaveToContent}
          className="btn-ghost text-xs inline-flex items-center gap-1.5"
          data-testid={`draft-save-${messageId}`}
        >
          <Save className="w-3.5 h-3.5" />
          Save to content board
        </button>
        <button
          onClick={onRegenerate}
          className="btn-ghost text-xs inline-flex items-center gap-1.5"
          data-testid={`draft-regen-${messageId}`}
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Regenerate
        </button>
        <a
          href="https://www.linkedin.com/feed/?shareActive=true"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-text text-xs inline-flex items-center gap-1 ml-auto"
          onClick={() => copyText(caption, "caption")}
          data-testid={`draft-open-linkedin-${messageId}`}
          title="copies post + opens LinkedIn composer"
        >
          open LinkedIn <ArrowRight className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function fmtRel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}
